/**
 * Subscription / plans / plafonds API. Gateway prefixes /api/v1/plans + /api/v1/subscription.
 */
import { api } from '@/lib/api/client';

export interface Plan {
  code: string;
  name: string;
  description: string | null;
  price_monthly: number;
  currency: string;
  ai_credits_monthly: number;
  overage_credit_price: number;
  limits: Record<string, number>;
  /** Boolean capability flags gating whole features (L-entitlements). */
  features: Record<string, boolean>;
  is_active: boolean;
  sort_order: number;
}

export interface UsageMetric {
  used: number;
  limit: number | null;
}
export interface CreditUsage {
  used: number;
  limit: number;
  balance: number;
  overage_credits: number;
  overage_cost: number;
}
export interface MySubscription {
  subscription: {
    plan_code: string;
    status: string; // active | past_due | canceled
    started_at: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    can_cancel: boolean; // a paid Stripe subscription not already scheduled to end
    has_billing: boolean; // a Stripe customer exists → the billing portal is available
  };
  plan: Plan | null;
  usage: { projects: UsageMetric; ai_credits: CreditUsage };
  limits: Record<string, number>;
  /** Boolean capability flags of the current plan (L-entitlements). */
  features: Record<string, boolean>;
  /** Where to send the user to upgrade (feature/limit gating CTA). */
  upgrade_url: string;
}

export function listPlans(): Promise<Plan[]> {
  return api.get<Plan[]>('/api/v1/plans');
}
export function getMySubscription(): Promise<MySubscription> {
  return api.get<MySubscription>('/api/v1/subscription/me');
}
export function subscribe(
  planCode: string,
): Promise<{ success: boolean; plan_code?: string; checkout_required: boolean; checkout_url?: string }> {
  return api.post('/api/v1/subscription/subscribe', { plan_code: planCode });
}

export interface CancelResult {
  success: boolean;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  status: string;
}
/** Résilier l'abonnement payant en fin de période (cancel_at_period_end). */
export function cancelSubscription(): Promise<CancelResult> {
  return api.post<CancelResult>('/api/v1/subscription/cancel', {});
}
/** Ouvre une session du portail de facturation Stripe et renvoie son url. */
export function openBillingPortal(): Promise<{ url: string }> {
  return api.post<{ url: string }>('/api/v1/billing/portal', {});
}
export function updatePlan(code: string, patch: Partial<Plan>): Promise<Plan> {
  return api.patch<Plan>(`/api/v1/plans/${encodeURIComponent(code)}`, patch);
}
export function createPlan(input: Partial<Plan> & { code: string; name: string }): Promise<Plan> {
  return api.post<Plan>('/api/v1/plans', input);
}

// ---- Stripe billable-catalog publishing (admin) --------------------------

export interface StripeCatalogItem {
  kind: 'plan' | 'pack';
  code: string;
  name: string;
  amount_cents: number;
  currency: string;
  interval: string | null; // 'month' for plans, null for one-time packs
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  published: boolean;
  drift: boolean; // price changed since last publish → needs re-publish
  published_amount_cents: number | null;
}
export interface StripeCatalog {
  stripe_configured: boolean;
  livemode: boolean | null; // true=live keys, false=sandbox/test, null=unknown
  items: StripeCatalogItem[];
}
export interface PublishResultItem extends StripeCatalogItem {
  action: 'created' | 'updated' | 'unchanged';
}
export interface PublishResult {
  stripe_configured: boolean;
  livemode: boolean | null;
  items: PublishResultItem[];
}

export function getStripeCatalog(): Promise<StripeCatalog> {
  return api.get<StripeCatalog>('/api/v1/plans/stripe/catalog');
}
export function publishStripeCatalog(): Promise<PublishResult> {
  return api.post<PublishResult>('/api/v1/plans/stripe/publish', {});
}
